import React from 'react'
import moment from 'moment'
import { Link } from 'react-router-dom'
import LineChart from './LineChart'
import PieChart from './PieChart'
import StatsTable from './StatsTable'
import { BADGE_INFO } from '../Utils'

const ASSOCIATED_APPS = {
  fitbit: 'FitBit',
  'apple-health': 'Apple Health',
  'google-health': 'Google Health Connect',
}

function pieDataForDay(day) {
  if (!day || !day.stepsByIntensity) return null

  const x = Object.assign({}, day.stepsByIntensity)
  if (day.data && day.data.sleep) {
    x.inbed = 0
    x.asleep = 0
    for (const session of day.data.sleep) {
      const start = moment(session.startTime)
      const end = moment(session.endTime)
      const minutesSinceMidnight = end.diff(end.clone().startOf('day'), 'minutes')
      const minutesSinceStart = end.diff(start, 'minutes')
      const percentToday = Math.min(minutesSinceStart, minutesSinceMidnight) / minutesSinceStart
      const asleep = session.minutesAsleep
      const inbed = session.timeInBed - asleep
      x.asleep += asleep
      x.inbed += inbed
      x['0'] = Math.max(0, x['0'] - Math.round((inbed + asleep) * percentToday))
    }
  }
  return x
}

export default class ProfileContainer extends React.Component {
  state = {
    hoverColumn: null,
  }

  _updateMeTimer = null

  componentDidMount() {
    this.props.onRefreshMe()
    this._updateMeTimer = setTimeout(() => {
      this.props.onRefreshMe()
    }, 60 * 1000)
  }

  componentWillUnmount() {
    clearTimeout(this._updateMeTimer)
  }

  render() {
    const { me } = this.props
    const { hoverColumn } = this.state
    const daysNonzero = me.daily.filter((d) => d.steps !== 0).length

    let notice = null

    const hoverDayString =
      hoverColumn !== null
        ? moment()
            .startOf('day')
            .add(hoverColumn - 14, 'days')
            .format('MMMM D, YYYY')
        : null

    let pieChartNote = ''

    if (me.fitnessService === 'apple-health' || me.fitnessService === 'google-health') {
      pieChartNote += `Note: This is not a minute-by-minute steps intensity analysis. `
    }
    if (me.today && (hoverColumn === null || hoverColumn === 14)) {
      pieChartNote += `Updated ${moment(me.today.updatedAt).fromNow()}`
      if (moment().unix() - moment(me.today.updatedAt).unix() > 30 * 60) {
        pieChartNote += `. Open the ${
          ASSOCIATED_APPS[me.fitnessService]
        } app to sync the latest data from your device.`
      }
    }
    if (!me.today) {
      pieChartNote = `No data synced for today. Open the ${
        ASSOCIATED_APPS[me.fitnessService]
      } app to sync the latest data from your device.`
    }

    if (!me.profile['meld']) {
      notice = (
        <div className="inline-tip">
          <p style={{ fontSize: '0.85em' }}>
            You haven't completed a baseline evaluation. Add your MELD score, age, and other
            information to see tailored exercise videos.
          </p>
          <a
            href={`${process.env.REACT_APP_BASE_URL}/api/mobile/me/redirect-to-baseline-evaluation`}
            className="btn black block small"
          >
            Complete Baseline Evaluation
          </a>
        </div>
      )
    }

    if (!me.fitnessService) {
      notice = (
        <div className="inline-tip">
          <p style={{ fontSize: '0.85em' }}>
            You haven't linked a fitness tracker to your account! Connect one to track your steps
            and compete with other participants on the leaderboard.
          </p>
          <Link to="/mobile/welcome/connect" className="btn black block small">
            Connect fitness tracker
          </Link>
        </div>
      )
    }

    if (me.isAdmin) {
      notice = (
        <div className="inline-tip">
          <p style={{ fontSize: '0.85em', marginBottom: 0 }}>
            You're logged in as a program administrator. You will see all videos in the app. Sign in
            as a participant to complete a baseline evaluation and see tailored videos.
          </p>
        </div>
      )
    }

    const pieData = pieDataForDay(hoverColumn !== null ? me.daily[hoverColumn] : me.today)
    const pieTitle =
      hoverColumn !== null && hoverColumn !== 14 ? `${hoverDayString}` : `Today's Activity`

    return (
      <div className="screen profile screen-flex-wrapper">
        <div className="navigation-bar">
          <div className="title">{me.username}</div>
          <Link to="/mobile/profile/settings" className="button-item right">
            Settings
          </Link>
        </div>
        <div className="screen-scroll-content">
          {notice}
          {notice ? <div style={{ height: 30 }} /> : undefined}
          {pieData ? (
            <PieChart title={pieTitle} subtitle={pieChartNote} data={pieData} />
          ) : (
            <div className="pie-chart">
              <div className="title">{pieTitle}</div>
              <div className="empty" style={{ paddingTop: 40 }}>
                No data to display. Open the {ASSOCIATED_APPS[me.fitnessService]} app to submit your
                latest activity.
              </div>
            </div>
          )}
          <div style={{ height: 15 }} />
          <LineChart
            hoverColumn={hoverColumn}
            hoverDayString={hoverDayString}
            setHoverColumn={(v) => this.setState({ hoverColumn: v })}
            title={'Daily Steps'}
            data={me.daily.map((d) => d.steps || 0)}
            goal={me.goalSteps}
          />
          <div className="goal-text">
            <div />
            Goal: {Number(me.goalSteps).toLocaleString()} steps per day
          </div>
          <div style={{ height: 30 }} />
          <StatsTable
            stats={[
              {
                label: (
                  <span>
                    Total Steps <span className="meta">(All Time)</span>
                  </span>
                ),
                value: me.stats.stepsTotal,
              },
              {
                label: (
                  <span>
                    Average Steps <span className="meta">(Last 2 Weeks)</span>
                  </span>
                ),
                value: Math.ceil(me.stats.stepsTwoWeeks / daysNonzero),
              },
              {
                label: (
                  <span>
                    Videos Watched <span className="meta">(All Time)</span>
                  </span>
                ),
                value: me.stats.watchedTotal,
              },
              {
                label: (
                  <span>
                    Videos Watched <span className="meta">(Last 2 Weeks)</span>
                  </span>
                ),
                value: me.stats.watchedTwoWeeks,
              },
              {
                label: <span>Assigned Section</span>,
                value: me.assignedSection
                  ? me.assignedSection.title.replace(' Training', '')
                  : 'None',
              },
            ]}
          />
          <div style={{ height: 30 }} />
          <div className="badges">
            <div className="title">Badges</div>
            <div className="subtitle">
              Earn badges by reaching your goal steps and elevating your heart rate for 30 minutes a
              day.
            </div>
            <div className="grid">
              {BADGE_INFO.map(({ key, icon, label }) => (
                <div key={key} className={`griditem ${me.stats.badges[key] > 0 && 'awarded'}`}>
                  <div style={{ position: 'relative' }}>
                    <img src={icon} alt="icon" />
                    <div className="count">
                      {me.stats.badges[key] > 0 ? me.stats.badges[key] : undefined}
                    </div>
                  </div>
                  <div className="meta">{label}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ height: 80 }} />
        </div>
      </div>
    )
  }
}
